import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { friendlyAuthError } from "@/lib/auth-errors";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
  head: () => ({
    meta: [
      { title: "Reset password — MissionReady" },
      { name: "description", content: "Reset your MissionReady password. Enter your account email and we'll send you a secure reset link." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Reset password — MissionReady" },
      { property: "og:description", content: "Request a password reset link for your MissionReady account." },
      { property: "og:url", content: "https://missionready.dev/forgot-password" },
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/forgot-password" }],
  }),
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const redirectTo = typeof window !== "undefined" ? window.location.origin + "/reset-password" : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo });
    setBusy(false);
    // Never reveal whether an account exists: only surface throttling/connection
    // problems, and treat every other outcome as a neutral success.
    const msg = (error?.message ?? "").toLowerCase();
    if (error && (msg.includes("rate limit") || msg.includes("too many") || msg.includes("network") || msg.includes("failed to fetch"))) {
      return toast.error(friendlyAuthError(error));
    }
    setSent(true);
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-hero px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-card">
          <h1 className="font-display text-2xl font-semibold">Forgot password</h1>
          <p className="mt-1 text-sm text-muted-foreground">Enter your email and we'll send you a reset link.</p>
          {sent ? (
            <div className="mt-6 rounded-md border border-border bg-muted/40 p-4 text-sm">
              If an account exists for <strong>{email}</strong>, a reset link is on its way.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            <Link to="/login" className="text-primary underline underline-offset-2">Back to sign in</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
